import { PropsWithChildren } from "react";

import { BuilderBlock, isEditing } from "@builder.io/sdk-react";
import Image from "next/image";

import { Footer as UIFooter } from "@jti/ui/eva-components/Footer";
import { RenderLink } from "@jti/ui/eva-components/types/RenderLink.type";

import { usePageConfig } from "@/context/PageConfigContext";
import { EditableZone } from "@/libs/builder/Blocks";
import { useDisplayModelComponent } from "@/libs/builder/hooks/useDisplayModelComponent";
import { Link } from "@/libs/i18n";

import { FooterProps } from "./inputs/footerInputs";

import styles from "./Footer.module.scss";

type EditingZoneKey =
  | "firstEditingZone"
  | "secondEditingZone"
  | "topEditingZone"
  | "topBannerEditingZone";
type EditingZone = FooterProps[EditingZoneKey];

export const Footer = (props: PropsWithChildren<FooterProps>) => {
  const {
    children,
    builderBlock,
    topEditingZone,
    firstEditingZone,
    secondEditingZone,
    socialIcons,
    paymentIcons,
    enableTopBanner,
    topBannerEditingZone,
    withListComponent,
    theme,
    ...restProps
  } = props;

  const { breadcrumbsList = [] } = usePageConfig();

  const commonBuilderBlocksProps = {
    parent: builderBlock?.id,
  };

  const shouldDisplayModelComponent = useDisplayModelComponent("footer");

  const renderSocialIcon = ({ src, alt }: { src: string; alt: string }) => {
    return (
      <Image
        src={src}
        alt={alt}
        width={32}
        height={32}
        className={styles.iconImage}
      />
    );
  };

  const shouldRenderEditingZone = (editingZone?: EditingZone) =>
    Boolean(editingZone?.length) ||
    (isEditing() && shouldDisplayModelComponent);

  const renderEditingZone = (editingZone: EditingZone, propPath: string) =>
    shouldRenderEditingZone(editingZone) ? (
      <EditableZone
        {...commonBuilderBlocksProps}
        blocks={editingZone as BuilderBlock[]}
        blockPropPathSegments={propPath}
      />
    ) : null;

  const firstZone = renderEditingZone(firstEditingZone, "firstEditingZone");
  const secondZone = renderEditingZone(secondEditingZone, "secondEditingZone");
  const topZone = renderEditingZone(topEditingZone, "topEditingZone");
  const topHealthBanner = renderEditingZone(
    topBannerEditingZone,
    "topBannerEditingZone"
  );

  const isLogoDarkMode = theme === "dark" || !theme;
  return (
    <UIFooter
      {...restProps}
      theme={theme}
      renderLink={renderLink}
      breadcrumbsList={breadcrumbsList}
      logoProps={{ ...props.logoProps, isDarkMode: isLogoDarkMode }}
      renderFooterTopZone={() => topZone}
      renderFirstBottomZone={() => firstZone}
      renderSecondBottomZone={() => secondZone}
      socialIcons={socialIcons}
      paymentIcons={paymentIcons}
      renderSocialIcon={renderSocialIcon}
      enableTopBanner={enableTopBanner}
      renderTopBanner={() => topHealthBanner}
      withListComponent={withListComponent}
    >
      {children}
    </UIFooter>
  );
};

// TODO: should be replaced with LinkBehaviour context
export const renderLink = (props: RenderLink): JSX.Element | null => {
  const { href, ...rest } = props;

  if (!href) return null;

  return (
    <Link href={href} {...rest}>
      {props.children}
    </Link>
  );
};

export default Footer;
